/**
 * Keeps the mirror fresh without you thinking about it.
 *
 * There is no server-side cron (see the note at the top of lib/todoistSync.ts —
 * the sync runs in the browser to avoid Cloudflare's subrequest cap), so
 * "regularly" can only mean "while the app is open". This syncs on load and
 * whenever you come back to the tab, throttled so that flicking between windows
 * does not hammer Todoist.
 *
 * Todoist allows 1000 requests / 15 min per token and a sync costs about four,
 * so the interval is set for sanity rather than for the rate limit.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { appMode, pb } from "@/lib/pocketbase";
import { runTodoistSync } from "@/lib/todoistSync";

const MIN_INTERVAL_MS = 5 * 60_000;
const LAST_SYNC_KEY = "ph.lastSyncedAt";
/** Fired after a write to Todoist (Quick Add) so the new task shows now, not
 *  on the next focus. The sync lives in the shell; this reaches it from anywhere. */
export const SYNC_REQUEST_EVENT = "ph:sync-request";

export interface AutoSyncState {
  syncing: boolean;
  lastSyncedAt: string | null;
  error: string | null;
  /** Force a sync regardless of the throttle. */
  syncNow: () => Promise<void>;
}

export function useAutoSync(): AutoSyncState {
  const qc = useQueryClient();
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(
    () => localStorage.getItem(LAST_SYNC_KEY),
  );
  // A ref as well as state: two focus events in the same tick would both read
  // a stale state value and start two syncs.
  const running = useRef(false);

  const sync = useCallback(async (force: boolean) => {
    if (appMode === "demo" || running.current) return;
    const userId = (pb.authStore.model as { id?: string } | null)?.id;
    if (!userId) return;

    if (!force) {
      const last = localStorage.getItem(LAST_SYNC_KEY);
      if (last && Date.now() - new Date(last).getTime() < MIN_INTERVAL_MS) return;
    }

    running.current = true;
    setSyncing(true);
    try {
      const result = await runTodoistSync(userId);
      const at = result.finishedAt || new Date().toISOString();
      localStorage.setItem(LAST_SYNC_KEY, at);
      setLastSyncedAt(at);
      setError(result.errors[0] ?? null);
      await qc.invalidateQueries();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      running.current = false;
      setSyncing(false);
    }
  }, [qc]);

  useEffect(() => {
    void sync(false);
    const onFocus = () => void sync(false);
    const onRequest = () => void sync(true);
    window.addEventListener("focus", onFocus);
    window.addEventListener(SYNC_REQUEST_EVENT, onRequest);
    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener(SYNC_REQUEST_EVENT, onRequest);
    };
  }, [sync]);

  return { syncing, lastSyncedAt, error, syncNow: () => sync(true) };
}

/** "just now" / "12 min ago" / "3 h ago" */
export function sinceLabel(iso: string | null): string {
  if (!iso) return "never";
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  return hrs < 24 ? `${hrs} h ago` : `${Math.floor(hrs / 24)} d ago`;
}
